import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { editCancelledShiftData, getShiftData } from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

export const getNoticePeriod = (noticePeriod: number) => {
  switch (noticePeriod) {
    case 0:
      return "Less than 24 hours";
    case 1:
      return "Less than 48 hours";
    case 2:
      return "More than 3 days";
    case 3:
      return "Excused (Valid reason)";
  }
};

const CancelledShifts = () => {
  const { state } = useAuth();
  const [workers, setWorkers] = useState<any>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState<any>(null);
  const [newReason, setNewReason] = useState("");
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit } = useForm<any>({
    defaultValues: {
      name: "",
      noticePeriod: "",
    }
  });

  const getWorkers = async (data: any) => {
    const response: any = await getShiftData(state.user?.token, {
      noticePeriod: data.noticePeriod,
      name: data.name
    });
    setWorkers(response.data);
  };

  const handleClick = (userId: any) => {
    const url = `/pace-team/resourcing/profile?workerId=${userId}`;
    window.open(url, "_blank");
  };

  const handleOpenModal = (shift: any) => {
    setSelectedShift(shift);
    setNewReason(shift.reason || "");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedShift(null);
    setNewReason("");
  };

  const handleUpdateReason = async () => {
    if (!selectedShift) return;

    setLoading(true);
    try {
      const response = await editCancelledShiftData(state.user?.token, {
        id: selectedShift.id,
        reason: newReason
      });
      console.log(response)
      if(response?.data.success) {
        toast.success("Reason updated successfully");
        getWorkers({});
      }
      handleCloseModal();
    } catch (error) {
      console.error("Failed to update reason:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns: any = [
    {
      field: "workerName",
      headerName: "Full Name",
      flex: 1.3,
      renderCell: (params: any) => (
        <div
          onClick={() => handleClick(params.row.workerId)}
          style={{ cursor: "pointer" }}
        >
          {params.row.workerName}
        </div>
      ),
    },
    {
      field: "whatsAppName",
      headerName: "Team Name",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "jobDate",
      headerName: "Job Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => (
        <div>
          {format(new Date(params.row.jobDate || Date.now()), "dd/MM/yyyy")}
        </div>
      ),
    },
    {
      field: "cancellationDate",
      headerName: "Date Cancellation",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => (
        <div>
          {format(
            new Date(params.row.cancellationDate || Date.now()),
            "dd/MM/yyyy",
          )}
        </div>
      ),
    },
    {
      field: "noticePeriod",
      headerName: "Notice Period",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => getNoticePeriod(params.row.noticePeriod),
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1.5,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "cancelledByName",
      headerName: "Cancelled By",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => (
        <>
          {params.row.cancelledByName === 'N/A'
            ? "Not Specified"
            : `${params.row.cancelledByName}`}
        </>
      ),
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => (
        <Button
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenModal(params.row);
          }}
          style={{
            boxShadow: "none",
            backgroundColor: "#6c757d",
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  useEffect(() => {
    getWorkers({});
  }, []);

  return (
    <>
      <div
        className="form-content"
        style={{ justifyContent: "flex-start", marginLeft: 85 }}
      >
        <TextField
          label="Full Name"
          type="text"
          size="small"
          {...register("name")}
          style={{ width: "15%", margin: 10 }}
        />
        <TextField
          select
          size="small"
          label="Notice Period"
          fullWidth
          {...register("noticePeriod")}
          style={{ width: 300, margin: 10 }}
        >
          <MenuItem value={0}>Less than 24 hours</MenuItem>
          <MenuItem value={1}>Less than 48 hours</MenuItem>
          <MenuItem value={2}>More than 3 days</MenuItem>
          <MenuItem value={3}>Excused (Valid reason)</MenuItem>
        </TextField>
        <Button
          variant="contained"
          onClick={handleSubmit(getWorkers)}
          style={{ height: 40, margin: 10, boxShadow: "none", width: "10%" }}
        >
          Filter
        </Button>
      </div>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
      >
        <DialogTitle>
          Edit Cancellation Reason
          {selectedShift && (
            <Box sx={{ mt: 1, fontSize: '0.9rem', color: 'text.secondary' }}>
              {selectedShift.workerName} - {selectedShift.jobDate && format(new Date(selectedShift.jobDate), "dd/MM/yyyy")}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Reason"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              style={{
                fontSize: '1rem',
                fontFamily: 'inherit',
                borderRadius: '4px',
                resize: 'vertical'
              }}
              placeholder="Enter new reason..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateReason}
            variant="contained"
            style={{ boxShadow: "none" }}
            color="primary"
            disabled={loading || !newReason.trim()}
          >
            {loading ? 'Updating...' : 'Update Reason'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          mt: 3,
          height: "800px",
          width: "90%",
          margin: "0 auto"
        }}>
        <DataGrid
          rows={workers || []}
          columns={columns}
          disableRowSelectionOnClick
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 100,
              },
            },
          }}
          pageSizeOptions={[100]}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              fontSize: 15,
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#f0f0f0",
              color: "#000",
              fontWeight: "bold",
            },
            "& .disable-columns": {
              borderRight: "1px solid #bfbfbf",
            },
            "& .border-left": {
              borderLeft: "1px solid #bfbfbf",
            },
          }}
        />
      </Box>
    </>
  );
};

export default CancelledShifts;
